import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import React, { useEffect, useState } from "react"
const Section = ({
  title,
  isOpen,
  onToggle,
  selected = [],
  items = [],
  getNameByCode,
  onRemove,
  children,
}) => (
  <div>
    <button
      type='button'
      onClick={onToggle}
      className='w-full flex justify-between items-center text-sm font-medium text-gray-700 mb-2'
    >
      {title}
      <ChevronDownIcon
        className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
      />
    </button>

    {!isOpen && (
      <div className='min-h-[42px] border-2 border-gray-200 rounded-lg p-2'>
        {selected.length === 0 ? (
          <p className='text-sm text-gray-400'>No selection</p>
        ) : (
          <div className='flex flex-wrap gap-2'>
            {selected.map((code) => (
              <span
                key={code}
                className='inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm'
              >
                {getNameByCode(items, code)}
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove(code)
                  }}
                  className='hover:text-blue-900'
                >
                  <XMarkIcon className='w-4 h-4' />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    )}

    {isOpen && (
      <div className='animate-in fade-in slide-in-from-top-1 duration-200'>
        {children}
      </div>
    )}
  </div>
)

export const RegionalDropdown = ({
  selectedRegions,
  selectedBarangays,
  selectedProvinces,
  selectedMunicipalities,
  toggleSelection,
  getNameByCode,
  regions,
  provinces,
  barangays,
  municipalities,
  removeItem,
}) => {
  const [open, setOpen] = useState({
    regions: true,
    provinces: false,
    municipalities: false,
    barangays: false,
  })

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }))

  useEffect(() => {
    if (selectedRegions.length > 0) {
      setOpen((prev) => ({
        ...prev,
        provinces: true,
      }))
    }
  }, [selectedRegions])

  useEffect(() => {
    if (selectedProvinces.length > 0) {
      setOpen((prev) => ({
        ...prev,
        municipalities: true,
      }))
    }
  }, [selectedProvinces])

  useEffect(() => {
    if (selectedMunicipalities.length > 0) {
      setOpen((prev) => ({
        ...prev,
        barangays: true,
      }))
    }
  }, [selectedMunicipalities])

  return (
    <div className='grid grid-cols-2 gap-6'>
      <Section
        title='Regions *'
        isOpen={open.regions}
        onToggle={() => toggle("regions")}
        selected={selectedRegions}
        items={regions}
        getNameByCode={getNameByCode}
        onRemove={(code) => removeItem("regions", code)}
      >
        <div className='border-2 border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto'>
          {regions.map((region) => (
            <label
              key={region.code}
              className='flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer'
            >
              <input
                type='checkbox'
                checked={selectedRegions.includes(region.code)}
                onChange={() => toggleSelection("regions", region.code)}
              />
              <span className='text-sm'>{region.name}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section
        title='Provinces *'
        isOpen={open.provinces}
        onToggle={() => toggle("provinces")}
        selected={selectedProvinces}
        getNameByCode={getNameByCode}
        items={provinces}
        onRemove={(code) => removeItem("provinces", code)}
      >
        <div className='border-2 border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto'>
          {provinces.length === 0 ? (
            <p className='text-gray-500 text-sm'>Select regions first</p>
          ) : (
            provinces.map((province) => (
              <label
                key={province.code}
                className='flex gap-2 p-2 cursor-pointer'
              >
                <input
                  type='checkbox'
                  checked={selectedProvinces.includes(province.code)}
                  onChange={() => toggleSelection("provinces", province.code)}
                />
                {province.name}
              </label>
            ))
          )}
        </div>
      </Section>

      <Section
        title='Municipalities / Cities *'
        isOpen={open.municipalities}
        onToggle={() => toggle("municipalities")}
        selected={selectedMunicipalities}
        getNameByCode={getNameByCode}
        items={municipalities}
        onRemove={(code) => removeItem("municipalities", code)}
      >
        <div className='border-2 border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto'>
          {municipalities.length === 0 ? (
            <p className='text-gray-500 text-sm'>Select provinces first</p>
          ) : (
            municipalities.map((m) => (
              <label key={m.code} className='flex gap-2 p-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={selectedMunicipalities.includes(m.code)}
                  onChange={() => toggleSelection("municipalities", m.code)}
                />
                {m.name}
              </label>
            ))
          )}
        </div>
      </Section>

      <Section
        title='Barangays *'
        isOpen={open.barangays}
        onToggle={() => toggle("barangays")}
        getNameByCode={getNameByCode}
        selected={selectedBarangays}
        items={barangays}
        onRemove={(code) => removeItem("barangays", code)}
      >
        <div className='border-2 border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto'>
          {barangays.length === 0 ? (
            <p className='text-gray-500 text-sm'>Select municipalities first</p>
          ) : (
            barangays.map((b) => (
              <label key={b.code} className='flex gap-2 p-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={selectedBarangays.includes(b.code)}
                  onChange={() => toggleSelection("barangays", b.code)}
                />
                {b.name}
              </label>
            ))
          )}
        </div>
      </Section>
    </div>
  )
}
